/**
 * api_request_failed: every request the dashboard makes that did not go
 * through, reported from authFetch so the ~570 call sites need nothing of
 * their own.
 *
 * What is reported:
 *   - a create, update or delete that failed, whatever the status, because
 *     that is a person trying to do something and not being able to;
 *   - a read on a 5xx or a network failure, or one the server called malformed
 *     (400, 405, 415, 422): that is the dashboard calling the API wrongly. A
 *     404 or 403 on a read is usually the page working as designed (a report
 *     not generated yet, a RoleGate expecting it), and reporting those would
 *     bury the real ones.
 *   - a 401 only as the FINAL answer. authFetch refreshes and retries the first
 *     one, so what reaches here is a session the server still refused with a
 *     fresh token: an expired or revoked sign-in, category `auth`.
 *   - a token refresh that failed outright (noteAuthRefreshFailed), so a
 *     sign-in service that cannot be reached shows up too.
 *
 * Not reported (report-gate.ts): a request its caller marked
 * `reportFailure: false`, and a network failure while offline, hidden or
 * unloading, or within a minute of the last one reported.
 *
 * Nothing that identifies a customer leaves: ids in the path become `:id` and
 * the query string is dropped. The response body is read for its error code
 * and its message, and the message is scrubbed (scrubErrorText) before it is
 * sent, because the message is what the person was shown.
 */

import { failureCategory, failureCategoryForStatus, scrubErrorText, type PropsFor } from "@fastn/core";
import { capture } from "./posthog";
import { noteFailure } from "./error-analytics";
import { isUnreported, shouldReportNetworkFailure, __resetReportGateForTests } from "./report-gate";

const MUTATIONS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Consecutive failures of one request this session, cleared when it succeeds. */
const failures = new Map<string, number>();

/**
 * The api request that failed last, reported or not. An error shown a moment
 * later is usually about it, and the error's own text often cannot say which
 * request it was: a network failure reads "Failed to fetch" on every page.
 */
export interface FailedRequest {
  /** `METHOD /api/v1/route`, ids as `:id`. */
  request: string;
  /** Null when no response came back. */
  status: number | null;
  at: number;
}
let lastFailed: FailedRequest | null = null;
/** An error shown later than this after the failure is not taken to be about it. */
const RELATED_MS = 3_000;

export function recentFailedRequest(now: number = Date.now()): FailedRequest | null {
  return lastFailed && now - lastFailed.at <= RELATED_MS ? lastFailed : null;
}

function isIdSegment(seg: string): boolean {
  if (/^\d+$/.test(seg)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(seg)) return true;
  // ucl:<workspace>:<endOrg>:..., emails, and anything with an encoded separator
  if (/[:@%]/.test(seg)) return true;
  // Prefixed ids: wf_..., stp_..., evt_..., cfg_..., conn_..., alert_...
  if (/^[a-z]{2,12}_[A-Za-z0-9-]{6,}$/.test(seg)) return true;
  if (/^[0-9a-f]{12,}$/i.test(seg)) return true;
  // Long mixed tokens (share codes, hashes) that are not words or slugs.
  return seg.length >= 20 && /\d/.test(seg);
}

/**
 * The request path from its API version on, as `/api/vN/...`, with every id
 * replaced by `:id`. Most call sites address `${API_BASE}/v1/...`, which the
 * server rewrites to `/api/v1/...` on arrival, so both spellings, behind any
 * base path, land on the one the server routes.
 */
export function routePattern(url: string): string {
  let path: string;
  try {
    path = new URL(url, "http://local").pathname;
  } catch {
    return "unknown";
  }
  const version = /(?:^|\/)(?:api\/)?(v\d+)(?=\/|$)/.exec(path);
  if (version) path = `/api/${version[1]}${path.slice(version.index + version[0].length)}`;
  return path
    .split("/")
    .map((seg) => (seg && isIdSegment(seg) ? ":id" : seg))
    .join("/");
}

function errorMessageOf(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  const nested = b.error && typeof b.error === "object" ? (b.error as Record<string, unknown>).message : b.error;
  return scrubErrorText(b.message ?? nested ?? b.detail ?? null);
}

function errorCodeOf(body: unknown): string | null {
  if (!body || typeof body !== "object") return null;
  const b = body as Record<string, unknown>;
  const nested = b.error && typeof b.error === "object" ? (b.error as Record<string, unknown>).code : undefined;
  const code = b.code ?? b.errorCode ?? nested;
  return typeof code === "string" && code.length <= 80 ? code : null;
}

/** By status, except a plan limit, which shares 402/429 with ordinary
 *  throttling and is a commercial event rather than a technical one. An
 *  unregistered code would resolve to `internal` by code, so status wins. */
function categoryOf(code: string | null, status: number | null) {
  if (code && failureCategory(code) === "quota") return "quota" as const;
  return failureCategoryForStatus(status);
}

type FailedProps = PropsFor<"api_request_failed">;

/** Reads the server refused as malformed: the caller's mistake, never a design. */
const MALFORMED = new Set([400, 405, 415, 422]);
/** Reads refused by a plan limit or a rate limit: the person was blocked. */
const BLOCKED = new Set([402, 429]);

function online(): boolean {
  return typeof navigator === "undefined" || navigator.onLine !== false;
}

function shouldReport(method: string, status: number | null): boolean {
  if (status !== null && status < 400) return false;
  if (MUTATIONS.has(method) || status === 401) return true;
  return status === null || status >= 500 || MALFORMED.has(status) || BLOCKED.has(status);
}

/**
 * Note how one request ended. `res` is null when the request never got a
 * response (network failure). Never throws and never consumes the caller's body.
 */
export function noteRequestOutcome(url: string, init: RequestInit | undefined, res: Response | null): void {
  try {
    const route = routePattern(url);
    if (!route.startsWith("/api/")) return;
    const method = (init?.method ?? "GET").toUpperCase();
    const key = `${method} ${route}`;
    const status = res ? res.status : null;
    if (res && res.ok) {
      failures.delete(key);
      return;
    }
    lastFailed = { request: key, status, at: Date.now() };
    if (!shouldReport(method, status) || isUnreported(init)) return;
    if (!res && !shouldReportNetworkFailure("request")) return;
    const attempt = (failures.get(key) ?? 0) + 1;
    failures.set(key, attempt);
    if (MUTATIONS.has(method)) noteFailure();
    const browserOnline = online();

    const send = (code: string | null, message: string | null = null) => {
      const props: FailedProps = {
        route,
        method,
        http_status: status,
        error_code: code,
        failure_category: categoryOf(code, status),
        is_network_error: res === null,
        attempt_number: attempt,
        error_message: res === null ? "Network request failed" : message,
        browser_online: browserOnline,
      };
      capture("api_request_failed", props);
    };

    if (!res) {
      send(null);
      return;
    }
    res.clone().json().then((body) => send(errorCodeOf(body), errorMessageOf(body)), () => send(null));
  } catch {
    // Telemetry must never be the reason a request fails.
  }
}

/**
 * authFetch could not get a token to send: Keycloak was unreachable or refused
 * the refresh. Reported once per route like any other failure.
 */
export function noteAuthRefreshFailed(url: string, init: RequestInit | undefined): void {
  try {
    const route = routePattern(url);
    if (!route.startsWith("/api/")) return;
    const method = (init?.method ?? "GET").toUpperCase();
    const key = `${method} ${route}`;
    lastFailed = { request: key, status: 401, at: Date.now() };
    const attempt = (failures.get(key) ?? 0) + 1;
    failures.set(key, attempt);
    const props: FailedProps = {
      route,
      method,
      http_status: 401,
      error_code: "AUTH_REFRESH_FAILED",
      failure_category: "auth",
      is_network_error: false,
      attempt_number: attempt,
      error_message: "The sign-in could not be refreshed",
      browser_online: online(),
    };
    capture("api_request_failed", props);
  } catch {
    // Telemetry must never be the reason a request fails.
  }
}

/** Test seam. */
export function __resetRequestFailuresForTests(): void {
  failures.clear();
  lastFailed = null;
  __resetReportGateForTests();
}
